import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { AgentCombobox } from "@/components/Agents/AgentCombobox";
import { useFieldErrors } from "@/components/Tools/use-field-errors";
import { useToast } from "@/hooks/use-toast";
import { type PhoneNumber, useUpdatePhoneNumber } from "@/hooks/use-phone-numbers";
import {
  editNumberErrors,
  type PhoneNumberField,
  type Refusal,
  refusalOf,
  refusedFields,
} from "@/utils/phone-number-rules";

interface EditPhoneNumberDialogProps {
  /** Mounted open, once per number: the form starts from it. */
  phoneNumber: PhoneNumber;
  onClose: () => void;
}

export default function EditPhoneNumberDialog({ phoneNumber, onClose }: EditPhoneNumberDialogProps) {
  const { toast } = useToast();
  const updatePhoneNumber = useUpdatePhoneNumber();
  const [form, setForm] = useState(() => ({
    inboundAgentId: phoneNumber.inbound_agent_id ?? "",
    outboundAgentId: phoneNumber.outbound_agent_id ?? "",
    maxCps: String(phoneNumber.max_cps),
    inboundWebhookUrl: phoneNumber.inbound_webhook_url ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<Refusal>();
  const contentRef = useRef<HTMLDivElement>(null);

  // Only the SIP-trunk path calls the inbound webhook: a Twilio number does not
  // show the field.
  const supportsInboundWebhook = phoneNumber.provider === "livekit_sip";

  const ruleErrors = editNumberErrors({
    maxCps: form.maxCps,
    inboundWebhookUrl: supportsInboundWebhook ? form.inboundWebhookUrl : "",
  });
  const errors = { ...ruleErrors, ...refusedFields(refusal, form) };
  const { field, error, check } = useFieldErrors<PhoneNumberField>(errors);

  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async () => {
    setRefusal(undefined);
    if (!check(contentRef.current, ruleErrors)) return;
    setSaving(true);
    try {
      await updatePhoneNumber.mutateAsync({
        id: phoneNumber.id,
        updates: {
          inbound_agent_id: form.inboundAgentId || null,
          outbound_agent_id: form.outboundAgentId || null,
          max_cps: Number(form.maxCps),
          // A Twilio number never calls one, so a stored URL — legacy data — is cleared.
          ...(supportsInboundWebhook
            ? { inbound_webhook_url: form.inboundWebhookUrl.trim() || null }
            : phoneNumber.inbound_webhook_url ? { inbound_webhook_url: null } : {}),
        },
      });
      toast({ title: "Phone number updated", description: `${phoneNumber.phone_number} has been saved.` });
      onClose();
    } catch (failure) {
      setRefusal(refusalOf(failure, form, "The phone number could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  // A save in flight finishes in view.
  const handleClose = () => {
    if (!saving) onClose();
  };

  /** The picker for one direction. While it still shows the row's agent, the
   * row's name labels it; once another is picked the combobox resolves the new one. */
  const agentPicker = (direction: "inbound" | "outbound", label: string) => {
    const name = `${direction}AgentId` as const;
    const id = `phone-edit-${direction}-agent`;
    const stored = form[name] && form[name] === phoneNumber[`${direction}_agent_id`];
    return (
      <div className="grid gap-2">
        <Label htmlFor={id}>{label}</Label>
        <AgentCombobox
          id={id}
          workspaceId={phoneNumber.workspace_id}
          value={form[name] || null}
          onChange={(agentId) => set(name, agentId ?? "")}
          placeholder={`Select agent for ${direction} calls`}
          clearLabel="No agent assigned"
          selectedLabel={stored ? phoneNumber[`${direction}_agent_name`] ?? undefined : undefined}
          disabled={saving}
        />
        {error(name, id)}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent ref={contentRef} className="sm:max-w-[500px] [&_[aria-invalid=true]]:border-destructive">
        <DialogHeader>
          <DialogTitle>Edit Phone Number</DialogTitle>
          <DialogDescription>Update settings for {phoneNumber.phone_number}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="phone-max-cps">Calls per second (CPS)</Label>
            <Input
              id="phone-max-cps"
              data-testid="phone-max-cps"
              type="number"
              min="0.1"
              step="0.1"
              value={form.maxCps}
              onChange={(event) => set("maxCps", event.target.value)}
              disabled={saving}
              {...field("maxCps", "phone-max-cps")}
            />
            {error("maxCps", "phone-max-cps")}
            <p className="text-xs text-muted-foreground">
              Max outbound calls per second dialed from this number across all campaigns.
              Most carriers default to 1 CPS; raise only if your SIP trunk allows it.
            </p>
          </div>
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Agent Assignments</h4>
            <div className="grid gap-4">
              {agentPicker("inbound", "Inbound Calls Agent")}
              {agentPicker("outbound", "Outbound Calls Agent")}
            </div>
          </div>
          {supportsInboundWebhook && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Inbound Call Webhook</h4>
              <div className="grid gap-2">
                <Label htmlFor="phone-inbound-webhook">Webhook URL (POST)</Label>
                <Input
                  id="phone-inbound-webhook"
                  data-testid="phone-inbound-webhook"
                  type="url"
                  placeholder="https://api.yourdomain.com/verbeo-inbound"
                  value={form.inboundWebhookUrl}
                  onChange={(event) => set("inboundWebhookUrl", event.target.value)}
                  disabled={saving}
                  {...field("inboundWebhookUrl", "phone-inbound-webhook")}
                />
                {error("inboundWebhookUrl", "phone-inbound-webhook")}
                <p className="text-xs text-muted-foreground">
                  Called once per inbound call, before the agent picks up. Return
                  dynamic variables to personalize the call and an optional
                  override_agent_id to route to a different agent. Must be HTTPS and
                  reply within 5s — otherwise the call connects normally on the
                  inbound agent above.
                </p>
              </div>
            </div>
          )}
        </div>
        {refusal && !refusal.field && (
          <p role="alert" data-testid="phone-save-error" className="text-sm text-destructive">
            {refusal.message}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving} data-testid="phone-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} aria-busy={saving} data-testid="phone-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
